import Dns from "../apis/Dns";

const RECORDS_CACHE = new Map();

function domainKeyOf(domain) {
	return domain?.id ?? domain?.domain ?? JSON.stringify(domain);
}
function getCached(domainKey, what) {
	const entry = RECORDS_CACHE.get(domainKey);
	return entry && Object.prototype.hasOwnProperty.call(entry, what) ? entry[what] : null;
}
function setCached(domainKey, what, state) {
	const entry = RECORDS_CACHE.get(domainKey) || {};
	entry[what] = state;
	RECORDS_CACHE.set(domainKey, entry);
}

export default {
	methods: {
		initEmailDomainInfo(domain) {
			domain.spf_info = domain.spf_info || {};
			domain.dkim_info = domain.dkim_info || {};

			if (typeof domain.spf_info.loading === "undefined") domain.spf_info.loading = true;
			if (typeof domain.spf_info.state === "undefined") domain.spf_info.state = false;

			if (typeof domain.dkim_info.loading === "undefined") domain.dkim_info.loading = true;
			if (typeof domain.dkim_info.state === "undefined") domain.dkim_info.state = false;

			if (Object.prototype.hasOwnProperty.call(domain, "spf_records")) {
				domain.spf_info.records = Array.isArray(domain.spf_records) ? domain.spf_records : [];
				delete domain.spf_records;
			} else {
				domain.spf_info.records = Array.isArray(domain.spf_info.records) ? domain.spf_info.records : [];
			}

			if (Object.prototype.hasOwnProperty.call(domain, "dkim_records")) {
				domain.dkim_info.records = Array.isArray(domain.dkim_records) ? domain.dkim_records : [];
				delete domain.dkim_records;
			} else {
				domain.dkim_info.records = Array.isArray(domain.dkim_info.records) ? domain.dkim_info.records : [];
			}

			domain.records_info = domain.records_info || { loading: true, state: null };
			if (typeof domain.records_info.loading === "undefined") domain.records_info.loading = true;
			if (typeof domain.records_info.state === "undefined") domain.records_info.state = null;

			return domain;
		},

		async checkRecords(domain) {
			if (!domain) return;
			if (!domain.records_info) {
				domain.records_info = { loading: true, state: null };
			}
			domain.records_info.loading = true;

			const key = domainKeyOf(domain);

			const cachedSpf = getCached(key, "spf");
			const cachedDkim = getCached(key, "dkim");
			if (typeof cachedSpf === "boolean" && typeof cachedDkim === "boolean") {
				domain.spf_info.state = cachedSpf;
				domain.spf_info.loading = false;
				domain.dkim_info.state = cachedDkim;
				domain.dkim_info.loading = false;
				this.updateDomainOverallState(domain);
				return;
			}

			await Promise.all([this.checkDkimRecords(domain), this.checkSpfRecords(domain)]);
			this.updateDomainOverallState(domain);
		},

		async checkDkimRecords(domain) {
			const dkim = domain?.dkim_info;
			if (!dkim || !Array.isArray(dkim.records)) {
				this.updateDomainOverallState(domain);
				return;
			}

			const key = domainKeyOf(domain);
			const cached = getCached(key, "dkim");
			if (typeof cached === "boolean") {
				dkim.state = cached;
				dkim.loading = false;
				this.updateDomainOverallState(domain);
				return;
			}

			dkim.loading = true;
			let result = false;
			try {
				const txtValues = await Promise.all(
					dkim.records.map(r => Dns.resolveTXTRecord(r.name))
				);
				const results = txtValues.map((txt, i) => {
					const exists = typeof txt === "string" && txt.trim() !== "";
					return exists && this.normalizeTxt(txt) === this.normalizeTxt(dkim.records[i].value);
				});
				result = results.every(Boolean);
			} catch (e) {
				result = false;
			}

			dkim.state = result;
			dkim.loading = false;
			setCached(key, "dkim", result);
			this.updateDomainOverallState(domain);
		},

		normalizeTxt(txt) {
			if (!txt || typeof txt !== 'string') return '';
			let normalized = txt.replace(/^"+|"+$/g, '');
			normalized = normalized.replace(/"\s+"/g, '');
			normalized = normalized.replace(/"/g, '').replace(/\s+/g, ' ').trim();
			return normalized;
		},

		isValidSpfSyntax(txt) {
			const normalized = this.normalizeTxt(txt);
			if (!/^v=spf1\b/i.test(normalized)) return false;

			const parts = normalized.split(/\s+/);
			const tokens = parts.slice(1);

			const mech = /^(?:[+\-~?])?(?:all|ip4:[^\s]+|ip6:[^\s]+|a(?::[^\s]+)?(?:\/\d{1,2})?|mx(?::[^\s]+)?(?:\/\d{1,2})?|ptr(?::[^\s]+)?|exists:[^\s]+|include:[^\s]+)$/i;
			const mod = /^(?:redirect|exp)=.+$/i;

			return tokens.length > 0 && tokens.every(t => mech.test(t) || mod.test(t));
		},

		async checkSpfRecords(domain) {
			const spf = domain?.spf_info;
			if (!spf || !Array.isArray(spf.records)) {
				this.updateDomainOverallState(domain);
				return;
			}

			const key = domainKeyOf(domain);
			const cached = getCached(key, "spf");
			if (typeof cached === "boolean") {
				spf.state = cached;
				spf.loading = false;
				this.updateDomainOverallState(domain);
				return;
			}

			spf.loading = true;
			let result = false;
			try {
				const txtValues = await Promise.all(
					spf.records.map(r => Dns.resolveTXTRecord(r.name))
				);
				const results = txtValues.map((txt) => {
					const exists = typeof txt === "string" && txt.trim() !== "";
					return exists && this.isValidSpfSyntax(txt);
				});
				result = results.every(Boolean);
			} catch (e) {
				result = false;
			}
			spf.state = result;
			spf.loading = false;
			setCached(key, "spf", result);
			this.updateDomainOverallState(domain);
		},

		updateDomainOverallState(domain) {
			if (!domain) return;
			const dkim = domain.dkim_info || {};
			const spf = domain.spf_info || {};

			const loading = Boolean(dkim.loading) || Boolean(spf.loading);
			const state = dkim.state === true && spf.state === true;

			if (!domain.records_info) {
				domain.records_info = { loading: true, state: null };
			}
			domain.records_info.loading = loading;
			domain.records_info.state = loading ? null : state;

			const key = domainKeyOf(domain);
			setCached(key, "overall", domain.records_info.state);
		},

		async checkAllDomains(domains) {
			if (!Array.isArray(domains) || !domains.length) return true;
			await Promise.all(domains.map((d) => this.checkRecords(d)));
			return domains.every((d) => d.records_info && d.records_info.state === true);
		},
	}
}